import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

export const SELECT_INVALID_SOLDO_FIELD = 'soldo-field-layout:has(.ng-invalid)';

export function scrollToFirstResult(...cssQueries: string[]): void {
    const compoundQuery = cssQueries.join(', ');

    const scrollToElement = document.querySelector(compoundQuery);

    if (scrollToElement) {
        scrollToElement.scrollIntoView({ behavior: 'smooth' });
        const inputElement = scrollToElement.querySelector('input, textarea, ng-select');
        if(typeof inputElement['focus'] === 'function') {
            setTimeout(() => {
                (<HTMLElement>inputElement).focus();
            }, 500);
        }
    }
}

export interface FormErrorsSummary {
    invalidFields: string[];
    asyncValidatorPending: boolean;
}

export const ROOT_FORM_FIELD = '(form)';
const FIELD_ERROR_SEPARATOR = ':';
const ERROR_KEY_SEPARATOR = '|';

/**
 * Collects invalid controls as `field:error1|error2` entries (names and error keys, never values), recursing into nested groups/arrays.
 * Array indices are collapsed to `*` so rows aggregate; `asyncValidatorPending` is set when a leaf control is still being validated and has no errors.
 */
export function collectFormErrors(control: AbstractControl, extraErrors: { [field: string]: string } = {}): FormErrorsSummary {
    const errorsByField = new Map<string, Set<string>>();
    let asyncValidatorPending = false;

    const addErrors = (field: string, keys: string[]): void => {
        const existing = errorsByField.get(field) ?? new Set<string>();
        keys.forEach(k => existing.add(k));
        errorsByField.set(field, existing);
    };

    const walk = (current: AbstractControl, path: string): void => {
        if (current.disabled) {
            return;
        }
        const fieldName = path || ROOT_FORM_FIELD;
        const keys = current.errors ? Object.keys(current.errors) : [];
        const children = current instanceof FormGroup || current instanceof FormArray
            ? Object.entries(current.controls)
            : [];

        if (!keys.length && current.pending && !children.length) {
            asyncValidatorPending = true;
        }
        if (keys.length) {
            addErrors(fieldName, keys);
        }

        for (const [name, child] of children) {
            const segment = current instanceof FormArray ? '*' : name;
            walk(child, path ? `${path}.${segment}` : segment);
        }
    };

    walk(control, '');

    for (const [field, key] of Object.entries(extraErrors)) {
        addErrors(field, [key]);
    }

    return {
        invalidFields: [...errorsByField].map(([field, keys]) => `${field}${FIELD_ERROR_SEPARATOR}${[...keys].join(ERROR_KEY_SEPARATOR)}`),
        asyncValidatorPending
    };
}
